import Image, { StaticImageData } from 'next/image';

import { H5 } from '@common/headline';
import { BodySmall } from '@common/body';

import selfGuidedWM from '@public/img/nhs/self-guided-wm-tile-icon.png';
import tier2WM from '@public/img/nhs/tier2wm-tile-icon.png';
import medicationSupportedWM from '@public/img/nhs/mspwm-tile-icon.png';
import tier3WM from '@public/img/nhs/tier3wm-tile-icon.png';
import diabetesPrevention from '@public/img/nhs/ndpp-tile-icon.png';
import type2Diabetes from '@public/img/nhs/t2dse-tile-icon.png';

const ServiceTile = ({
  image,
  title,
  body,
  link,
}: {
  image: StaticImageData;
  title: string;
  body: string;
  link: string;
}): JSX.Element => (
  <a href={link} target="_blank" rel="noopener noreferrer" className="block">
    <div className="mx-auto h-full max-w-md overflow-hidden rounded-3xl bg-white text-left drop-shadow-[0_20px_40px_rgba(9,37,48,0.07)] transition duration-300 ease-in-out hover:-translate-y-1 hover:shadow-lg">
      <div className="relative h-48 w-full">
        <Image src={image} layout="fill" alt="Service" objectFit="cover" />
      </div>
      <div className="flex grow flex-col p-6">
        <H5>{title}</H5>
        <div className="mt-1.5 overflow-hidden text-ellipsis">
          <BodySmall>{body}</BodySmall>
        </div>
      </div>
    </div>
  </a>
);

export const Services = (): JSX.Element => (
  <div className="relative w-full bg-dawn-blue-100 py-12 text-center text-blue lg:py-16">
    <div className="mx-auto">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-12">
        <ServiceTile
          image={selfGuidedWM}
          title="Self guided weight management"
          body="Our self guided app programme helps people to build healthy habits and lose weight at scale"
          link="https://partnerships.secondnature.io/self-guided"
        />
        <ServiceTile
          image={tier2WM}
          title="Tier 2 weight management"
          body="A more personalised service that provides additional lifestyle coaching from registered nutritionists and dietitians"
          link="https://partnerships.secondnature.io/tier2"
        />
        <ServiceTile
          image={medicationSupportedWM}
          title="Medication supported weight management"
          body="A multidisciplinary service combining GLP-1RA medication and lifestyle coaching for cost-effective and sustainable weight loss"
          link="https://partnerships.secondnature.io/glp1"
        />
        <ServiceTile
          image={tier3WM}
          title="Tier 3 weight management"
          body="An intensive multidisciplinary service that delivers personalised care for those living with complex obesity"
          link="https://partnerships.secondnature.io/tier3"
        />
        <ServiceTile
          image={diabetesPrevention}
          title="Type 2 diabetes prevention"
          body="An intensive service that helps people reduce their risk of developing type 2 diabetes with lifestyle coaching"
          link="https://partnerships.secondnature.io/diabetes-prevention"
        />
        <ServiceTile
          image={type2Diabetes}
          title="Type 2 diabetes support"
          body="A personalised service that helps people learn more about type 2 diabetes and lose weight to improve health outcomes"
          link="https://partnerships.secondnature.io/t2dm"
        />
      </div>
    </div>
  </div>
);
